import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { PackagingLotStockOutForm } from './new'
import type { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import type { CreateOutNoteRequest } from '@/lib/api/packaging-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { packagingNoteItemOption } from '@/components/lots/lot-option-builders'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    usePackagingOutNote,
    useUpdatePackagingOutNote,
} from '@/hooks/use-packaging-lot-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-out-notes/$id_/edit',
)({
    component: PackagingLotStockOutEditPage,
})

export function PackagingLotStockOutEditPage() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = usePackagingOutNote(id)
    const note = data?.data
    const update = useUpdatePackagingOutNote()

    const seedOrderOptions = useMemo(() => {
        if (!note?.order_id) return []
        return [
            {
                value: note.order_id,
                label: note.order_code ?? note.order_id,
            },
        ]
    }, [note])

    // Lô của phiếu đang sửa phải còn chọn/hiện được dù đã rơi khỏi "chỉ lô
    // còn tồn" — làm seed cho nguồn lô dùng chung.
    const seedLotOptions = useMemo<LotOption[]>(
        () => (note?.items ?? []).map(packagingNoteItemOption),
        [note],
    )

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/packaging-warehouse/stock-out-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const initialValues = {
        reason: note.reason,
        order_id: note.order_id ?? '',
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            packaging_lot_id: it.packaging_lot_id,
            quantity: it.quantity,
        })),
    }

    const backToDetail = () =>
        navigate({
            to: '/packaging-warehouse/stock-out-notes/$id',
            params: { id },
        })

    const handleSubmit = async (
        value: CreateOutNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        try {
            await workflow.run(
                () =>
                    update.mutateAsync({
                        id,
                        req: {
                            reason: value.reason,
                            notes: value.notes,
                            items: value.items,
                        },
                    }),
                (result) => result.data?.id,
            )
            void backToDetail()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PackagingLotStockOutForm
            mode="edit"
            title={m.pkl_pxk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            seedOrderOptions={seedOrderOptions}
            seedLotOptions={seedLotOptions}
            isPending={update.isPending}
            onCancel={() => void backToDetail()}
            onSubmit={handleSubmit}
        />
    )
}
